<script setup>
import {onMounted, reactive, watch, ref, defineAsyncComponent} from "vue";
    import {useRoute, useRouter} from "vue-router";
    import { usePartyStore } from "@/Stores/partyStore";
    import { useUserStore } from "@/Stores/userStore";
    import { useCreditStore } from "@/Stores/creditStore";
    import { useProgressStore } from "@/Stores/progressStore";
    import { useStatusStore } from "@/Stores/statusStore";
    import { useFeatureStore } from "@/Stores/featureStore";
    import { useUniversalChatStore } from "@/Stores/universalChatStore";
    import { initializeAbly } from "@/Api/api";
    import displayConstants from "@/Constants/display-constants";
    import {useToast} from "vue-toastification";
    import sessionsApi from "@/Api/sessions-api";
    import systemApi from "@/Api/system-api";
    import accountApi    from "@/Api/account-api";
    import featuresApi from "@/Api/features-api";
    import {useI18n} from "vue-i18n";
    import ThankYouModal from "@/Modals/ThankYouModal.vue";
    import GlobalStyles from "@/Pages/Style/GlobalStyles.vue";
    import CookieConsent from "@/Components/CookieConsent.vue";
    import clarityService from "@/Services/clarity";

    // Lazy-load heavy modal components
    const LoginModal = defineAsyncComponent(() => import("@/Modals/LoginModal.vue"));
    const RegistrationModal = defineAsyncComponent(() => import("@/Modals/RegistrationModal.vue"));

    const { t, locale } = useI18n({ useScope: 'global' })
    const router = useRouter();
    const route = useRoute();
    const toast = useToast();

    const chatStore = useUniversalChatStore();

    const data = reactive({
        displayThankYou: false,
    });

    const showLoginModal = ref(null);
    const showRegistrationModal = ref(null);


    const checkHash = () => {
       data.displayThankYou = route.hash === '#thankyou';
    };

    watch(() => route.fullPath, () => {
        checkHash();
    });

    onMounted(() => {
        const partyStore = usePartyStore();
        const userStore = useUserStore();
        userStore.initializeUserId();
        userStore.initializeSubscription();
        userStore.initializeAdaptiveAudioMinutes();
        updateSessions();
        updateStatus();

        watch(
            () => partyStore.partyId,
            async party => {
                if (party) {
                    await subscribeToPartyEvents(party);
                }
            },
            { immediate: true }
        )
        watch(
            () => userStore.userId,
            async userId => {
                if (userId) {
                    await subscribeToUserEvents(userId);
                    await updateFeatures();
                }
            },
            { immediate: true }
        )

        // This will show a toast when the user is redirected back from the payment gateway.
        // It may reappear if the user refreshes the page or navigates to another page and then back.
        if (location.hash === "#thankyou") {
            data.displayThankYou = true;
        }

        checkUrlLanguagePreference();
    })

    async function subscribeToPartyEvents(partyId) {
        const ably = await initializeAbly()

        const channelName = 'private:party.' + partyId;
        const channel = ably.channels.get(channelName);
        console.log('channel: ', channel)
        await channel.subscribe('content', (message) => {
            // In theory, we can direct content to a specific component from here.
            console.log('display-content: ', message)
            if (message.data.content_type === displayConstants.contentTypes.equipmentStash) {
                presentEquipmentStash(message.data.content_id);
            }

            if (message.data.content_type === displayConstants.contentTypes.merchant) {
                presentMerchant(message.data.content_id);
            }

            if (message.data.content_type === displayConstants.contentTypes.creature) {
                presentCreature(message.data.content_id);
            }

            if (message.data.content_type === displayConstants.contentTypes.character) {
                presentCharacter(message.data.content_id);
            }
        });

        await channel.subscribe('joined', (message) => {
            // In theory, we can direct content to a specific component from here.
            console.log('member joined: ', message)
            const joinedMessage = message.data.display_name + ' joined the party!';
            toast.success(joinedMessage);
        });
    }

    async function subscribeToUserEvents(userId) {
        const creditStore = useCreditStore()
        const progressStore = useProgressStore()
        const statusStore = useStatusStore()
        const ably = await initializeAbly()

        const channelName = 'private:credit.' + userId;
        const channel = ably.channels.get(channelName);
        console.log('channel: ', channel)
        await channel.subscribe('credit-updated', (message) => {
            console.log('credit-updated: ', message)
            creditStore.setCurrentBalance(message.data.balance);
            creditStore.setMaxBalance(message.data.max_balance)
            creditStore.setUnlimitedCredit(message.data.unlimited)
        });

        const progressChannelName = 'private:progress.' + userId;
        const progressChannel = ably.channels.get(progressChannelName);
        console.log('channel: ', progressChannel)
        await progressChannel.subscribe('updated', (message) => {
            console.log('progress-updated: ', message)
            progressStore.setEventMessage(message.data.message);
            console.log('progressStore: ', progressStore.getEventMessage())
        });

        // Only subscribing to status events as an authenticated user right now because currently we can't send events to non-authenticated users
        // As they don't have an ably token.
        const statusChannelName = 'public:system-status';
        const statusChannel = ably.channels.get(statusChannelName);
        console.log('channel: ', statusChannel)
        await statusChannel.subscribe('updated', (message) => {
            console.log('status-updated: ', message)
            statusStore.setStatus(message.data);
            console.log('statusStore: ', statusStore.getStatus())
        });
    }

    function presentEquipmentStash(stash_id) {
        console.log('presenting-equipment-stash: ', stash_id)
        router.push({ name: 'equipment-stash', query: { stash_id: stash_id } })
    }

    function presentMerchant(npc_id) {
        console.log('presenting-equipment-stash: ', npc_id)
        router.push({ name: 'merchant', query: { npc_id: npc_id } })
    }

    function presentCreature(creature_id) {
        console.log('creature: ', creature_id)
        router.push({ name: 'creature', query: { creature_id: creature_id } })
    }

    function presentCharacter(character_id) {
        console.log('character: ', character_id)
        router.push({ name: 'character', query: { id: character_id } })
    }

    function checkUrlLanguagePreference() {
        const urlParams = new URLSearchParams(window.location.search);
        const language = urlParams.get('language');
        console.log('language: ', language)
        if (language) {
            locale.value = language;
            localStorage.setItem('language-preference', language);
            return
        }

        // Set stored language preference if it exists.
        const savedLanguage = localStorage.getItem('language-preference');
        if (savedLanguage) {
            locale.value = savedLanguage;
        }
    }

    function updateSessions() {
        const urlParams = new URLSearchParams(window.location.search);
        const utmSource = urlParams.get('utm_source');
        const utmMedium = urlParams.get('utm_medium');
        const utmId = urlParams.get('utm_id');

        // Add parameters to your request if they exist
        const data = {};
        if (utmSource) data.utm_source = utmSource;
        if (utmMedium) data.utm_medium = utmMedium;
        if (utmId) data.utm_id = utmId;

        sessionsApi.postSession(data).then(response => {})
        updateFeatures()
    }

    function updateFeatures() {
        const featureStore = useFeatureStore();
        featuresApi.getFeatures().then(response => {
            const features = response.data.features;
            const alerts = response.data.alerts;
            featureStore.setFeatures(features);
            featureStore.setAlerts(alerts);
        });
    }

    function updateStatus() {
        const statusStore = useStatusStore();
        const localStatus = statusStore.getStatus()
        if (!localStatus) {
            systemApi.getSystemStatus().then(response => {
                const status = response.data;
                console.log('status: ', status)
                statusStore.setStatus(status);
            });
        }
    }

    function requestSocialAuthentication(code) {
        const creditStore = useCreditStore();
        const partyStore = usePartyStore();
        const userStore = useUserStore();
        const statusStore = useStatusStore()

        accountApi.socialAuthentication(code).then(response => {
            console.log(response)
            userStore.setAccessToken(response.data.token.access_token)
            userStore.setSocketTokenRequest(response.data.socket_token)
            userStore.setHasRegistered(true)
            userStore.setPermissions(response.data.user.permissions)
            userStore.setSubscription(response.data.subscription)
            userStore.setAdaptiveAudioMinutes(response.data.adaptive_audio_minutes)
            creditStore.setCurrentBalance(response.data.credit.balance)
            creditStore.setMaxBalance(response.data.credit.max_balance)
            creditStore.setUnlimitedCredit(response.data.credit.unlimited)
            statusStore.resetLapsedTime()

            // Track social authentication in Clarity
            const userId = userStore.getUserIdFromToken();
            const email = userStore.getEmailFromToken();
            clarityService.identifyUser(userId, email);
            clarityService.setSubscriptionTag(response.data.subscription);
            clarityService.trackEvent('login_success');

            if (response.data.redirect) {
                const urlRegex = /^(https?:\/\/)/;
                if (urlRegex.test(response.data.redirect)) {
                    // If it's a URL, use window.location.href
                    window.location.href = response.data.redirect;
                } else {
                    router.push({
                        name: response.data.redirect,
                        hash: '#generate'
                    });
                }
            } else {
                router.push({ name: 'welcome' });
            }
        }).catch(function (error) {
            console.log(error)
            const message = error.response?.data?.error ?? "Something went wrong. Please try again.";
            toast.error(message);
        });
    }


    watch(
        () => route.query.code,
        async code => {
            if (code) {
                requestSocialAuthentication(code)
            }
        },
        { immediate: true }
    )

    // Watch for auth request from chat
    watch(
        () => chatStore.authModalRequested,
        (requested) => {
            if (requested === 'registration') {
                showRegistrationModal.value = true;
            } else if (requested === 'login') {
                showLoginModal.value = true;
            }
            // Reset the request
            if (requested) {
                chatStore.clearAuthModalRequest();
            }
        }
    );

    // Watch for auth query param (e.g. redirected from /login or /register)
    watch(
        () => route.query.auth,
        (auth) => {
            if (auth === 'register') {
                showRegistrationModal.value = true;
            } else if (auth === 'login') {
                showLoginModal.value = true;
            }
            if (auth) {
                router.replace({ query: { ...route.query, auth: undefined } });
            }
        },
        { immediate: true }
    );

    function closeLoginModal() {
        showLoginModal.value = null;
    }

    function openRegistrationFromLogin() {
        showLoginModal.value = null;
        showRegistrationModal.value = true;
    }

    function closeRegistrationModal() {
        showRegistrationModal.value = null;
    }

    function openLoginFromRegistration() {
        showRegistrationModal.value = null;
        showLoginModal.value = true;
    }

    function handleLoginSuccess() {
        closeLoginModal();
        closeRegistrationModal();
        // Reopen chat modal after successful login
        chatStore.openModal();
    }

    function handleRegistrationSuccess() {
        closeRegistrationModal();
        // Reopen chat modal after successful registration
        chatStore.openModal();
    }
</script>

<template>
    <div class="main-component">

        <ThankYouModal v-if="data.displayThankYou" :show="data.displayThankYou" @close="data.displayThankYou = false"/>

        <!-- Cookie Consent Banner -->
        <CookieConsent />

        <!-- Auth Modals -->
        <LoginModal
            :show="showLoginModal"
            @close="closeLoginModal"
            @register="openRegistrationFromLogin"
            @login-success="handleLoginSuccess"
        />

        <RegistrationModal
            :show="showRegistrationModal"
            @close="closeRegistrationModal"
            @login="openLoginFromRegistration"
            @registration-success="handleRegistrationSuccess"
        />

        <main>
            <router-view/>
        </main>
    </div>
</template>

